import { createRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Lock01 } from "@untitledui/icons";
import { Route as RootRoute } from "../__root";
import { requireTenant } from "../../lib/authGuard";
import { AppShell, type AppNavSection } from "../../components/app/AppShell";
import { useAuthStore, type Role } from "../../lib/authStore";
import { ThemeMenuControl } from "../../components/app/ThemeMenuControl";
import { apiCall } from "../../lib/apiClient";

/**
 * Tenant shell — sjednocený AppShell (oficiální Untitled UI sidebar navigace).
 *
 * Sidebar adaptivní per role:
 *   - subAdmin (Milan) / tenantAdmin → vidí sekci Mikroskupina.
 *   - endUser → pouze Moje (Dashboard + karty).
 * Mobile: UUI sidebar drawer (≤ lg) přebírá navigaci.
 */
export const Route = createRoute({
  getParentRoute: () => RootRoute,
  id: "tenant-layout",
  beforeLoad: ({ location }) => requireTenant({ location: { href: location.href } }),
  component: TenantLayoutComponent,
});

const ROLE_LABELS: Record<Role, string> = {
  globalAdmin: "Globální admin",
  tenantAdmin: "Admin tenantu",
  subAdmin: "Sub-admin",
  endUser: "Uživatel",
};

function TenantLayoutComponent() {
  const auth = useAuthStore((s) => s.auth);
  const resetAuth = useAuthStore((s) => s.reset);
  const navigate = useNavigate();
  const [, setLoggingOut] = useState(false);

  const role: Role = auth.state === "authenticated" ? auth.user.role : "endUser";
  const userName = auth.state === "authenticated" ? auth.user.email : "Uživatel";

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await apiCall("/api/public/auth/logout", { method: "POST", body: JSON.stringify({}) });
    } catch {
      // Ignore — local state se vyresetuje a redirect proběhne bez ohledu na server.
    }
    resetAuth();
    void navigate({ to: "/" });
  }

  const nav: AppNavSection[] = [
    {
      title: "Moje",
      items: [
        { to: "/tenant/dashboard", label: "Dashboard", icon: "dashboard" },
        { to: "/tenant/my-cards", label: "Moje karty", icon: "payment-account" },
      ],
    },
  ];

  if (role === "tenantAdmin") {
    nav.push({
      title: "Správa střediska",
      items: [
        { to: "/tenant/cards", label: "Všechny karty", icon: "payment-account" },
        { to: "/tenant/holders", label: "Držitelé", icon: "billing-group" },
        { to: "/tenant/budgets", label: "Rozpočty", icon: "budget" },
        { to: "/tenant/users", label: "Uživatelé", icon: "microgroup-requests" },
        { to: "/tenant/sync-status", label: "Stav dat", icon: "sync" },
      ],
    });
  }

  if (role === "subAdmin") {
    // tenantAdmin má vlastní „Správa střediska → Uživatelé/Pozvánky"; sekce
    // Mikroskupina (Členové/Pozvat) je sub-admin funkce a pro tenantAdmina matoucí.
    nav.push({
      title: "Mikroskupina",
      items: [
        { to: "/tenant/microgroup", label: "Členové", icon: "microgroup-requests" },
        { to: "/tenant/microgroup/invite", label: "Pozvat", icon: "microgroup-requests" },
      ],
    });
  }

  return (
    <AppShell
      nav={nav}
      account={{
        name: userName,
        email: ROLE_LABELS[role],
        onSignOut: () => void handleLogout(),
        menuTop: <ThemeMenuControl />,
        menuItems: [
          {
            label: "Změnit heslo",
            icon: Lock01,
            onClick: () => void navigate({ to: "/profile/change-password" }),
          },
        ],
      }}
    >
      <Outlet />
    </AppShell>
  );
}
